import { FilterChip, FilterRow } from './Chat.styled';

type Props = {
  unansweredOnly: boolean;
  onUnansweredOnlyChange: (value: boolean) => void;
  unreadOnly: boolean;
  onUnreadOnlyChange: (value: boolean) => void;
  archived: boolean;
  onArchivedChange: (value: boolean) => void;
  groupsOnly: boolean;
  onGroupsOnlyChange: (value: boolean) => void;
  /** Merchants tab only - the one list that holds groups beside support. */
  showGroups: boolean;
  /** Groups with something unread, shown inside the Groups chip. */
  unreadGroups?: number;
  /** Hidden on the internal half - only a support thread waits on an answer. */
  showUnanswered: boolean;
  /** The chip's word, which matches the marker on the rows it finds. */
  unansweredLabel?: string;
  /** Threads with something unread in this half, shown inside the chip. */
  unreadThreads?: number;
};

/**
 * The inbox filters, as chips under the search.
 *
 * They used to live behind a sliders button, which meant their state was a red
 * dot on an icon: an empty list under a filter somebody had forgotten reads as
 * "no conversations", and finding out why took a click. On the surface they say
 * what they are doing while they do it, and turning one off is the same click
 * that turned it on.
 *
 * "All" is not a filter of its own - it is the absence of the other two, which
 * is why it is derived rather than stored. That also makes it the initial
 * state without anything having to select it, and it can never disagree with
 * the chips beside it the way a third boolean eventually would.
 *
 * Which KIND of conversation you are looking at is the coarser cut and belongs
 * to the tabs above; what is left here is genuine narrowing of the one list.
 */
export const ChatFilters = ({
  unansweredOnly,
  onUnansweredOnlyChange,
  unreadOnly,
  onUnreadOnlyChange,
  archived,
  onArchivedChange,
  showUnanswered,
  unansweredLabel = 'Waiting',
  unreadThreads,
  groupsOnly,
  onGroupsOnlyChange,
  showGroups,
  unreadGroups,
}: Props) => {
  const all = !unansweredOnly && !unreadOnly && !archived && !groupsOnly;

  /** Past 99 the number stops being a number and becomes a shape. */
  const count = (value?: number) =>
    value ? ` ${value > 99 ? '99+' : value}` : '';

  return (
    <FilterRow>
      <FilterChip
        as="button"
        type="button"
        $selected={all}
        /* Already the whole list when nothing is on, so this only ever has
           something to do while a filter is. */
        onClick={() => {
          if (unansweredOnly) onUnansweredOnlyChange(false);
          if (unreadOnly) onUnreadOnlyChange(false);
          if (archived) onArchivedChange(false);
          if (groupsOnly) onGroupsOnlyChange(false);
        }}
      >
        All
      </FilterChip>

      {showUnanswered && (
        <FilterChip
          as="button"
          type="button"
          $selected={unansweredOnly}
          onClick={() => onUnansweredOnlyChange(!unansweredOnly)}
        >
          {/* "Waiting", not "Waiting for reply": the row it filters for wears
              that exact word as its own marker, and the long form pushed the
              fourth chip out of a 300px column. */}
          {unansweredLabel}
        </FilterChip>
      )}

      <FilterChip
        as="button"
        type="button"
        $selected={unreadOnly}
        onClick={() => onUnreadOnlyChange(!unreadOnly)}
      >
        {/* The count rides inside the chip rather than beside it: it is the
            size of what this filter would leave you with. */}
        Unread
        {count(unreadThreads)}
      </FilterChip>

      {/* A kind of thread rather than a state of one, but on the Merchants tab
          the groups share one list with the support queue, and this is the
          way to see them on their own. */}
      {showGroups && (
        <FilterChip
          as="button"
          type="button"
          $selected={groupsOnly}
          onClick={() => onGroupsOnlyChange(!groupsOnly)}
        >
          Groups
          {count(unreadGroups)}
        </FilterChip>
      )}

      {/* Last, and on both halves: the shelf holds support threads and groups
          alike, so it narrows whichever list you are on rather than replacing
          it. Everything else here asks "which of these are still open?" - this
          one asks for the ones that are not. */}
      <FilterChip
        as="button"
        type="button"
        $selected={archived}
        onClick={() => onArchivedChange(!archived)}
      >
        Archived
      </FilterChip>
    </FilterRow>
  );
};
